import { type ImageLoaderProps } from 'next/image';

import { buildContentstackUrl } from '@/app/components/media/app-image/shared/contentstack-url';
import { normalizeSrc } from '@/app/components/media/app-image/shared/image-source';
import {
  type ImageKitParams,
  type ImageKitUrlOptions,
} from '@/app/components/media/app-image/shared/imagekit-types';
import { buildImageKitUrl } from '@/app/components/media/app-image/shared/imagekit-url';
import { nextImageKitLoader } from '@/app/components/media/app-image/shared/next-imagekit-loader';
import {
  IMAGE_PROJECT_CONFIG,
  type ImageRuntimeEnv,
} from '@/app/components/media/app-image/shared/project-config';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { stripHtmlTags } from '@/app/utils/format';
import { isImageKitUrl } from '@/app/utils/imagekit';

// Project Types
export type ImageTrackingProps = {
  testId?: string;
};
export type { ImageRuntimeEnv };

// DOM Attributes
export const getImageAttrs = (input: {
  testid?: string;
}): Record<string, string> => createDataAttributes({ test: input.testid });

// Alt Text
export const sanitizeImageAlt = (alt: string): string => stripHtmlTags(alt);

// ImageKit
export const getProjectImageKitDefaultTransforms = (): readonly string[] =>
  IMAGE_PROJECT_CONFIG.imageKit.defaultTransforms;

export const getProjectImageKitUrlOptions = (
  params?: ImageKitParams
): ImageKitUrlOptions => ({
  defaultTransforms: getProjectImageKitDefaultTransforms(),
  params,
});

/**
 * Project URL builder: ImageKit hosts use `tr=` transforms;
 * other remotes (e.g. Contentstack) use delivery query params.
 */
export const buildProjectImageUrl = (
  srcInput: unknown,
  opts: ImageKitUrlOptions = {}
): string => {
  const src = normalizeSrc(srcInput);
  if (!src) return '';

  if (isImageKitUrl(src)) return buildImageKitUrl(src, opts);
  return buildContentstackUrl(src, opts.params);
};

/** Backward-compatible alias used by existing call sites / barrel export. */
export const buildUrlWithParams = (
  srcInput: unknown,
  opts: { params?: ImageKitParams } = {}
): string =>
  buildProjectImageUrl(srcInput, getProjectImageKitUrlOptions(opts.params));

export const nextProjectImageLoader = ({
  src,
  width,
  quality,
}: ImageLoaderProps): string => {
  if (!src) return '';

  if (!isImageKitUrl(src)) {
    return buildContentstackUrl(src, {
      ...(typeof width === 'number' && width > 0 ? { w: width } : {}),
      ...(typeof quality === 'number' && quality > 0 ? { q: quality } : {}),
    });
  }

  return nextImageKitLoader({ src, width, quality });
};

// Resource Hints
const ROOT_ASSET_HINT_ENV_SET = new Set<string>(
  IMAGE_PROJECT_CONFIG.resourceHints.rootAssetHintEnvs
);

export const shouldUseRootAssetResourceHintForEnv = (
  runtimeEnv: ImageRuntimeEnv
): boolean => ROOT_ASSET_HINT_ENV_SET.has(runtimeEnv);
